import cx from 'classnames';
import { useCallback, useContext, useEffect, useState } from 'react';
import DsModal from '@/common/DsModal';
import { SectionNameToLevelMap } from '~/constants/new-cars/SectionName';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import { MatchingVehiclesProvider } from '~/context/new-cars/MatchingVehiclesContext';
import { useModalContext } from '~/context/new-cars/ModalContext/ModalContext';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import useClosestVehicle from '~/hooks/new-cars/selectors/useClosestVehicle';
import useTotalMatchingVehicles from '~/hooks/new-cars/selectors/useTotalMatchingVehicles';
import useVehicleName from '~/hooks/new-cars/selectors/useVehicleName';
import ContentHeader from './ContentHeader';
import MatchingVehiclesView from './MatchingVehiclesView';
import {
  Body,
  Content,
  PanelColumn,
  VehiclesWrapper
} from './ViewVehiclesModal.styles';
import YourSelectionsPanel from './YourSelectionsPanel';

export default function ViewVehiclesModal() {
  const { activeSection } = useContext(ParentModelContext);
  const [showModalHeader, setShowModalHeader] = useState(true);

  const { isViewVehiclesModalOpen, setViewVehiclesModalOpen } =
    useModalContext();
  const isExactMatch = Boolean(useClosestVehicle());
  const totalMatchingVehicles = useTotalMatchingVehicles();
  const trackEvent = useTrackEvent();
  const vehicleName = useVehicleName();

  const onClose = useCallback(
    () => setViewVehiclesModalOpen(false),
    [setViewVehiclesModalOpen]
  );

  const onIntersectionChange = useCallback((isIntersecting: boolean) => {
    setShowModalHeader(!isIntersecting);
  }, []);

  useEffect(() => {
    if (!isViewVehiclesModalOpen) return;

    trackEvent('New Cars', {
      action: 'viewed',
      level: SectionNameToLevelMap[activeSection],
      section: 'Matching Vehicles Modal',
      totalMatchingVehicles
    });
  }, [
    activeSection,
    isViewVehiclesModalOpen,
    totalMatchingVehicles,
    trackEvent
  ]);

  return (
    <DsModal
      classes={{
        body: 'pt-0',
        container: cx(
          'bg-background-weak',
          !isExactMatch &&
            'sm:max-w-[1800px] sm:w-[calc(100vw-(2*var(--spacing-4xl)))] sm:h-[calc(100vh-(2*var(--spacing-4xl)))] sm:max-h-[unset]'
        ),
        header: 'bg-background-weak'
      }}
      desktopWidth={isExactMatch ? '800px' : '1200px'}
      isOpen={isViewVehiclesModalOpen}
      modalHeader={showModalHeader ? vehicleName : <>&nbsp;</>}
      mobileStyle="full"
      onClose={onClose}
    >
      <MatchingVehiclesProvider>
        <Body>
          <ContentHeader onIntersectionChange={onIntersectionChange} />
          <Content isExactMatch={isExactMatch}>
            <PanelColumn isExactMatch={isExactMatch}>
              <YourSelectionsPanel />
            </PanelColumn>
            <VehiclesWrapper isExactMatch={isExactMatch}>
              <MatchingVehiclesView />
            </VehiclesWrapper>
          </Content>
        </Body>
      </MatchingVehiclesProvider>
    </DsModal>
  );
}
